import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GroupsPickerDropdownPanel from './GroupsPickerDropdownPanel';
import { computeFloatingPanel } from './computeFloatingPanelBelow';

const Z_INDEX = 99999;

/**
 * Assign To / Assigned Roles picker — portaled to document.body (create & edit modes).
 */
const GroupsPickerDropdownPortal = forwardRef(function GroupsPickerDropdownPortal(
  {
    isOpen,
    anchorRef,
    title,
    subtitle,
    count = 0,
    minWidth = 256,
    children,
  },
  ref
) {
  const internalPanelRef = useRef(null);
  const panelRef = ref || internalPanelRef;
  const [position, setPosition] = useState(null);

  const syncPosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return null;
    const panelEl = panelRef.current;
    const height = panelEl ? panelEl.offsetHeight : 280;
    return computeFloatingPanel(el, { minWidth, estimatedHeight: height });
  }, [anchorRef, minWidth, panelRef]);

  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current || !anchorRef?.current) return;
    const height = panelRef.current.offsetHeight || 280;
    const next = computeFloatingPanel(anchorRef.current, { minWidth, estimatedHeight: height });
    if (next) setPosition(next);
  }, [isOpen, minWidth, anchorRef, children, count, panelRef]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }

    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  if (!isOpen || !position || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <GroupsPickerDropdownPanel
      ref={panelRef}
      variant="portal"
      title={title}
      subtitle={subtitle}
      count={count}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex: Z_INDEX,
      }}
    >
      {children}
    </GroupsPickerDropdownPanel>,
    document.body
  );
});

export default GroupsPickerDropdownPortal;
